import { useBrokerContext } from '@/app/context';
import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import {
  AttachBusinessQuotePayload,
  AttachQuotePayload,
} from '@/services/quotes/types';
import { useAdvisoryOptions } from '@/shared/utils';
import {
  Alert,
  Button,
  Dialog,
  Field,
  Select,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { ArrowLeft, Calculator, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useQuoteParams } from '../../context/quote-params.context';
import { useQuoteSummary } from '../../context/quote-summary.context';
import {
  isRnsAnalysisRequiredError,
  isRnsUnavailableError,
  isUnderwritingAnalysisRequiredError,
  useCalculateBusinessQuote,
} from '../../hooks/business/use-calculate-quote.hook';
import { useUpdateBusinessQuote } from '../../hooks/business/use-update-quote.hook';
import { useCalculateQuote } from '../../hooks/homeowner/use-calculate-quote.hook';
import { useCommissionLimits } from '../../hooks/homeowner/use-commission-limits.hook';
import { withForm } from '../../hooks/common/use-quote-form.hook';
import { useUpdateQuote } from '../../hooks/homeowner/use-update-quote.hook';

type CoBroker = {
  id: string;
  coBrokerId: string;
  coBrokerName: string;
  participationPercent: string;
};

function normalizeCoBrokerList(coBrokers: CoBroker[]) {
  const seen = new Set<string>();

  return coBrokers.filter(coBroker => {
    const identifier = (coBroker.coBrokerId || coBroker.coBrokerName)
      .trim()
      .toLowerCase();

    if (!identifier || seen.has(identifier)) {
      return false;
    }

    seen.add(identifier);
    return true;
  });
}

function parsePercentageInput(value: string) {
  const normalized = value.replace(',', '.').trim();
  if (!normalized) return NaN;
  return Number(normalized);
}

function sanitizePercentageInput(value: string) {
  let hasSeparator = false;

  return value
    .replace(/\s/g, '')
    .split('')
    .filter(char => {
      if (/\d/.test(char)) {
        return true;
      }

      if ((char === ',' || char === '.') && !hasSeparator) {
        hasSeparator = true;
        return true;
      }

      return false;
    })
    .join('');
}

const formOpts = formOptions({
  defaultValues: {
    commissionPercent: '',
    brokerName: '',
    participationPercent: '',
    includeCoBrokerage: false,
    coBrokers: [] as CoBroker[],
  },
});

export const StepCommercialization = withForm({
  ...formOpts,
  props: {
    onNext: (_id?: string) => {
      void _id;
    },
    onBack: () => {},
    prefillCommissionPercent: '',
    prefillBrokerName: '',
    prefillParticipationPercent: '',
    prefillCoBrokers: [] as Array<{
      id?: string;
      name?: string;
      shareRate?: number;
    }>,
    prefillBrokerAgentCode: '',
    stepCode: '',
    isViewMode: false,
    skipBackendSubmit: false,
    /** Pending externo (ex.: calculate-analysis disparado pelo pai no fluxo de análise). */
    isSubmitting: false,
  },
  render: ({
    form,
    onNext,
    onBack,
    prefillCommissionPercent,
    prefillBrokerName,
    prefillParticipationPercent,
    prefillCoBrokers,
    prefillBrokerAgentCode,
    stepCode,
    isViewMode,
    skipBackendSubmit,
    isSubmitting,
  }) => {
    const { id, productCode, category } = useQuoteParams();
    const isBusinessQuote = category === 'BUSINESS_INSURANCE';
    const { mutateAsync: mutateHomeownerAsync, isPending: isHomeownerPending } =
      useUpdateQuote();
    const { mutateAsync: mutateBusinessAsync, isPending: isBusinessPending } =
      useUpdateBusinessQuote();
    const mutateAsync = isBusinessQuote
      ? mutateBusinessAsync
      : mutateHomeownerAsync;
    const isPending = isBusinessQuote ? isBusinessPending : isHomeownerPending;
    const homeownerCalculateMutation = useCalculateQuote();
    const businessCalculateMutation = useCalculateBusinessQuote();
    const calculateMutation = isBusinessQuote
      ? businessCalculateMutation
      : homeownerCalculateMutation;
    const { updateCommercialization } = useQuoteSummary();
    const { selectedBroker } = useBrokerContext();
    const { data: commissionLimits } = useCommissionLimits();
    const minCommission = commissionLimits?.minCommission ?? 1;
    const maxCommission = commissionLimits?.maxCommission ?? 40;
    const [coBrokers, setCoBrokers] = useState<CoBroker[]>(
      form.state.values.coBrokers || []
    );
    const [coBrokerErrors, setCoBrokerErrors] = useState<
      Record<string, { coBroker?: string; participationPercent?: string }>
    >({});
    const [calculationError, setCalculationError] = useState<string | null>(
      null
    );
    const [participationSumError, setParticipationSumError] = useState<
      string | null
    >(null);
    const [rnsAnalysisBanner, setRnsAnalysisBanner] = useState<string | null>(
      null
    );
    const [rnsHighClaimDialogOpen, setRnsHighClaimDialogOpen] = useState(false);
    const pendingNavigateAfterRnsWarningRef = useRef<string | null>(null);
    const prevIncludeCoBrokerage = useRef(form.state.values.includeCoBrokerage);
    const hasInitializedCommercialization = useRef(false);
    const { items: brokerOptions, isLoading: isLoadingBrokers } =
      useAdvisoryOptions({
        search: '',
        pageSize: 1000,
        partyType: 'broker',
        brokerId: selectedBroker?.entityId,
        productCode,
      });

    useEffect(() => {
      const includeCoBrokerage = form.state.values.includeCoBrokerage;
      if (prevIncludeCoBrokerage.current !== includeCoBrokerage) {
        prevIncludeCoBrokerage.current = includeCoBrokerage;

        if (includeCoBrokerage) {
          setCoBrokers(prev => {
            if (prev.length === 0) {
              const newCoBroker: CoBroker = {
                id: Date.now().toString(),
                coBrokerId: '',
                coBrokerName: '',
                participationPercent: '',
              };
              form.setFieldValue('coBrokers', [newCoBroker]);
              return [newCoBroker];
            }
            return prev;
          });
        } else {
          setCoBrokers([]);
          form.setFieldValue('coBrokers', []);
        }
      }
    }, [form.state.values.includeCoBrokerage, form]);

    // Sem co-corretagem, o corretor principal detém 100% da participação e o
    // campo permanece fixo em 100,00 (não editável).
    useEffect(() => {
      if (
        !form.state.values.includeCoBrokerage &&
        form.state.values.participationPercent !== '100,00'
      ) {
        form.setFieldValue('participationPercent', '100,00');
      }
    }, [
      form,
      form.state.values.includeCoBrokerage,
      form.state.values.participationPercent,
    ]);

    const handleAddCoBroker = () => {
      const newCoBroker: CoBroker = {
        id: Date.now().toString(),
        coBrokerId: '',
        coBrokerName: '',
        participationPercent: '',
      };
      const updatedCoBrokers = [...coBrokers, newCoBroker];
      setCoBrokers(updatedCoBrokers);
      form.setFieldValue('coBrokers', updatedCoBrokers);
    };

    const handleRemoveCoBroker = (idToRemove: string) => {
      const updatedCoBrokers = coBrokers.filter(cb => cb.id !== idToRemove);
      setCoBrokers(updatedCoBrokers);
      form.setFieldValue('coBrokers', updatedCoBrokers);
      setCoBrokerErrors(prev => {
        const nextErrors = { ...prev };
        delete nextErrors[idToRemove];
        return nextErrors;
      });

      if (updatedCoBrokers.length === 0) {
        form.setFieldValue('includeCoBrokerage', false);
      }
    };

    const handleCoBrokerChange = (
      id: string,
      field: keyof CoBroker,
      value: string
    ) => {
      const resolvedValue =
        field === 'participationPercent'
          ? sanitizePercentageInput(value)
          : value;

      setCoBrokers(prev => {
        const updatedCoBrokers = prev.map(cb =>
          cb.id === id ? { ...cb, [field]: resolvedValue } : cb
        );
        form.setFieldValue('coBrokers', updatedCoBrokers);
        return updatedCoBrokers;
      });

      setParticipationSumError(null);

      setCoBrokerErrors(prev => {
        const current = prev[id] || {};
        if (field === 'participationPercent') {
          return {
            ...prev,
            [id]: { ...current, participationPercent: undefined },
          };
        }
        if (field === 'coBrokerId' || field === 'coBrokerName') {
          return { ...prev, [id]: { ...current, coBroker: undefined } };
        }
        return prev;
      });
    };

    const handleCoBrokerSelection = (id: string, selectedId: string) => {
      const selectedBroker = brokerOptions.find(
        item => item.value === selectedId
      );
      setCoBrokers(prev => {
        const updatedCoBrokers = prev.map(cb =>
          cb.id === id
            ? {
                ...cb,
                coBrokerId: selectedId,
                coBrokerName: selectedBroker?.label || '',
              }
            : cb
        );
        form.setFieldValue('coBrokers', updatedCoBrokers);
        return updatedCoBrokers;
      });

      setCoBrokerErrors(prev => {
        const current = prev[id] || {};
        return { ...prev, [id]: { ...current, coBroker: undefined } };
      });
    };

    useEffect(() => {
      const brokerName = selectedBroker?.name || '';
      if (brokerName && !form.state.values.brokerName) {
        form.setFieldValue('brokerName', brokerName);
      }
    }, [selectedBroker, form]);

    useEffect(() => {
      if (hasInitializedCommercialization.current) {
        return;
      }

      const currentFormCoBrokers = Array.isArray(form.state.values.coBrokers)
        ? (form.state.values.coBrokers as CoBroker[])
        : [];
      const hasExistingCoBrokerState =
        form.state.values.includeCoBrokerage || currentFormCoBrokers.length > 0;
      const hasPrefill =
        !!prefillBrokerName ||
        !!prefillCommissionPercent ||
        !!prefillParticipationPercent ||
        prefillCoBrokers.length > 0;

      if (hasExistingCoBrokerState) {
        setCoBrokers(currentFormCoBrokers);
        form.setFieldValue(
          'includeCoBrokerage',
          currentFormCoBrokers.length > 0 ||
            form.state.values.includeCoBrokerage
        );
      } else if (hasPrefill) {
        const mappedPrefillCoBrokers: CoBroker[] = prefillCoBrokers
          .filter(coBroker => Boolean((coBroker.name || '').trim()))
          .map((coBroker, index) => ({
            id: String(coBroker.id || `${Date.now()}-${index}`),
            coBrokerId: String(coBroker.id || ''),
            coBrokerName: coBroker.name || '',
            participationPercent:
              coBroker.shareRate !== undefined
                ? String((coBroker.shareRate || 0) * 100)
                : '',
          }));

        if (!form.state.values.commissionPercent) {
          form.setFieldValue(
            'commissionPercent',
            prefillCommissionPercent || ''
          );
        }
        if (!form.state.values.brokerName) {
          form.setFieldValue('brokerName', prefillBrokerName || '');
        }
        if (!form.state.values.participationPercent) {
          form.setFieldValue(
            'participationPercent',
            prefillParticipationPercent || ''
          );
        }
        form.setFieldValue(
          'includeCoBrokerage',
          mappedPrefillCoBrokers.length > 0
        );
        form.setFieldValue('coBrokers', mappedPrefillCoBrokers);
        setCoBrokers(mappedPrefillCoBrokers);
        updateCommercialization({
          brokerName: prefillBrokerName,
          commissionPercent: prefillCommissionPercent,
          participationPercent: prefillParticipationPercent,
          coBrokers: mappedPrefillCoBrokers.map(coBroker => ({
            name: coBroker.coBrokerName,
            participationPercent: coBroker.participationPercent,
          })),
        });
      } else {
        if (!form.state.values.participationPercent) {
          form.setFieldValue('participationPercent', '100,00');
        }
        form.setFieldValue('includeCoBrokerage', false);
        form.setFieldValue('coBrokers', []);
        setCoBrokers([]);
      }

      hasInitializedCommercialization.current = true;
    }, [
      form,
      prefillBrokerName,
      prefillCommissionPercent,
      prefillParticipationPercent,
      prefillCoBrokers,
      updateCommercialization,
    ]);

    useEffect(() => {
      if (brokerOptions.length === 0 || coBrokers.length === 0) {
        return;
      }

      const nextCoBrokers = coBrokers.map(cb => {
        if (cb.coBrokerId || !cb.coBrokerName.trim()) return cb;

        const matchedBroker = brokerOptions.find(
          option =>
            option.label.trim().toLowerCase() === cb.coBrokerName.toLowerCase()
        );

        if (!matchedBroker) return cb;

        return {
          ...cb,
          coBrokerId: matchedBroker.value,
          coBrokerName: matchedBroker.label,
        };
      });

      const hasChanged = nextCoBrokers.some(
        (cb, index) =>
          cb.coBrokerId !== coBrokers[index]?.coBrokerId ||
          cb.coBrokerName !== coBrokers[index]?.coBrokerName
      );

      if (!hasChanged) return;

      setCoBrokers(nextCoBrokers);
      form.setFieldValue('coBrokers', nextCoBrokers);
    }, [brokerOptions, coBrokers, form]);

    const handleSubmit = async () => {
      if (skipBackendSubmit) {
        const fields: Array<
          'commissionPercent' | 'brokerName' | 'participationPercent'
        > = ['commissionPercent', 'brokerName', 'participationPercent'];
        const results = await Promise.all(
          fields.map(field => form.validateField(field, 'change'))
        );
        const hasErrors = results.some(
          errors => Array.isArray(errors) && errors.length > 0
        );
        if (hasErrors) {
          return;
        }
        updateCommercialization({
          brokerName: form.state.values.brokerName,
          commissionPercent: form.state.values.commissionPercent,
          participationPercent: form.state.values.participationPercent,
        });
        onNext(id ?? '');
        return;
      }
      setParticipationSumError(null);

      // Valida todos os campos obrigatórios (exceto includeCoBrokerage)
      const fieldsToValidate: Array<
        'commissionPercent' | 'brokerName' | 'participationPercent'
      > = ['commissionPercent', 'brokerName', 'participationPercent'];
      const validationPromises = fieldsToValidate.map(fieldName =>
        form.validateField(fieldName, 'change')
      );
      const results = await Promise.all(validationPromises);
      let hasErrors = results.some(result => result.length > 0);

      const normalizedCoBrokers = normalizeCoBrokerList(coBrokers);

      if (form.state.values.includeCoBrokerage) {
        const newErrors: Record<
          string,
          { coBroker?: string; participationPercent?: string }
        > = {};

        // Quando o checkbox de co-corretagem está marcado, precisa de ao menos
        // um co-corretor cadastrado. Se não houver, força a validação a falhar.
        if (normalizedCoBrokers.length === 0) {
          hasErrors = true;
          setParticipationSumError(
            'Informe ao menos um co-corretor ou desmarque "Incluir co-corretagem".'
          );
        }

        normalizedCoBrokers.forEach(cb => {
          const errors: { coBroker?: string; participationPercent?: string } =
            {};

          // Validação do nome do co-corretor
          if (!cb.coBrokerId || cb.coBrokerName.trim() === '') {
            errors.coBroker = 'Este campo é obrigatório';
          }

          // Validação do percentual de participação
          if (
            !cb.participationPercent ||
            cb.participationPercent.trim() === ''
          ) {
            errors.participationPercent = 'Este campo é obrigatório';
          } else {
            const n = parseFloat(cb.participationPercent);
            if (Number.isNaN(n)) {
              errors.participationPercent = 'Informe um percentual válido';
            } else if (n <= 0 || n > 100) {
              errors.participationPercent =
                'Percentual deve ser maior que 0 e menor ou igual a 100';
            }
          }

          if (Object.keys(errors).length > 0) {
            newErrors[cb.id] = errors;
          }
        });

        if (Object.keys(newErrors).length > 0) {
          setCoBrokerErrors(newErrors);
          hasErrors = true;
        } else {
          setCoBrokerErrors({});
        }
      }

      // Só remove duplicados / co-corretores inválidos depois da validação,
      // para que entradas vazias gerem erro inline ao invés de serem
      // descartadas silenciosamente.
      const filteredCoBrokers = normalizedCoBrokers.filter(
        coBroker =>
          coBroker.coBrokerId !== selectedBroker?.code &&
          parsePercentageInput(coBroker.participationPercent) > 0 &&
          coBroker.coBrokerName.trim().toLowerCase() !==
            (form.state.values.brokerName || '').trim().toLowerCase()
      );

      if (!hasErrors && filteredCoBrokers.length !== coBrokers.length) {
        setCoBrokers(filteredCoBrokers);
        form.setFieldValue('coBrokers', filteredCoBrokers);
      }

      if (!hasErrors) {
        const mainPercent = parsePercentageInput(
          form.state.values.participationPercent
        );
        const mainVal = isNaN(mainPercent) ? 0 : mainPercent;
        const coPercentsSum = form.state.values.includeCoBrokerage
          ? filteredCoBrokers.reduce((sum, cb) => {
              const n = parsePercentageInput(cb.participationPercent);
              return sum + (isNaN(n) ? 0 : n);
            }, 0)
          : 0;
        const totalPercent = mainVal + coPercentsSum;

        if (Math.abs(totalPercent - 100) > 0.01) {
          setParticipationSumError(
            `A soma dos percentuais de participação deve ser 100%. Total atual: ${totalPercent.toFixed(2)}%`
          );
          hasErrors = true;
        }
      }

      if (!hasErrors) {
        try {
          setCalculationError(null);
          setRnsAnalysisBanner(null);

          const commissionPercent = parsePercentageInput(
            form.state.values.commissionPercent
          );
          const participationPercent = parsePercentageInput(
            form.state.values.participationPercent
          );
          const baseCommission = isNaN(commissionPercent)
            ? 0
            : commissionPercent / 100;
          const mainShareRate = isNaN(participationPercent)
            ? 0
            : participationPercent / 100;

          const includeCoBrokerage = form.state.values.includeCoBrokerage;

          const agents = [
            {
              name: form.state.values.brokerName,
              commission: baseCommission * mainShareRate,
              shareRate: mainShareRate,
              agentCode: selectedBroker?.code || prefillBrokerAgentCode || '',
              registerNumber: selectedBroker?.licenseNumber || '',
            },
            ...(includeCoBrokerage
              ? filteredCoBrokers.map(cb => {
                  const cbShareRate =
                    parsePercentageInput(cb.participationPercent) / 100;
                  return {
                    name: cb.coBrokerName,
                    commission: baseCommission * cbShareRate,
                    shareRate: cbShareRate,
                    agentCode: cb.coBrokerId,
                  };
                })
              : []),
          ];

          const payload: Partial<
            AttachQuotePayload | AttachBusinessQuotePayload
          > = {
            step: stepCode || 'COMMERCIALIZATION',
            agents,
          };

          if (!id) {
            setCalculationError(
              'ID da cotação não encontrado. Por favor, tente novamente.'
            );
            return;
          }

          const result = await mutateAsync({
            id,
            payload,
          });

          if (result.success) {
            updateCommercialization({
              brokerName: form.state.values.brokerName,
              commissionPercent: form.state.values.commissionPercent,
              participationPercent: form.state.values.participationPercent,
              coBrokers: includeCoBrokerage
                ? filteredCoBrokers.map(coBroker => ({
                    name: coBroker.coBrokerName,
                    participationPercent: coBroker.participationPercent,
                  }))
                : [],
            });

            try {
              const calcData = await calculateMutation.mutateAsync({
                policyId: id,
              });

              if (
                isBusinessQuote &&
                calcData &&
                '_rnsWarning' in calcData &&
                Boolean(
                  (calcData as { _rnsWarning?: boolean })._rnsWarning
                )
              ) {
                pendingNavigateAfterRnsWarningRef.current = id;
                setRnsHighClaimDialogOpen(true);
              } else {
                onNext(id);
              }
            } catch (error) {
              if (isRnsUnavailableError(error)) {
                setRnsAnalysisBanner(error.message);
                return;
              }
              if (isRnsAnalysisRequiredError(error)) {
                setRnsAnalysisBanner(error.message);
                return;
              }
              if (isUnderwritingAnalysisRequiredError(error)) {
                // Backend já gravou StatusPortal 17 (Cotação em Análise). Segue
                // para o step de cálculo, que carrega a lista de mensagens
                // de subscrição via GET /underwriting-messages.
                onNext(id);
                return;
              }
              const errorMessage =
                error instanceof Error
                  ? error.message
                  : 'Erro ao calcular cotação. Por favor, verifique os dados e tente novamente.';
              setCalculationError(errorMessage);
            }
          } else {
            setCalculationError(
              result.error ||
                'Erro ao atualizar cotação. Por favor, tente novamente.'
            );
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Erro inesperado ao processar cotação. Por favor, tente novamente.';
          setCalculationError(errorMessage);
        }
      }
    };

    return (
      <div className='flex flex-col gap-8 p-4 sm:p-6 lg:p-8 xl:p-12'>
        <div className='flex flex-col gap-8'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Comercialização
          </Text>

          {calculationError && (
            <Alert.Root status='error'>
              <Alert.Icon />
              <Alert.Content>
                <Alert.Title>Erro no Cálculo</Alert.Title>
                <Alert.Description>{calculationError}</Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}

          {rnsAnalysisBanner && (
            <Alert.Root status='warning'>
              <Alert.Icon />
              <Alert.Content>
                <Alert.Title>Análise da subscrição</Alert.Title>
                <Alert.Description>{rnsAnalysisBanner}</Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}

          <div className='flex flex-col gap-4'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              <form.AppField
                name='commissionPercent'
                validators={{
                  onChange: ({ value }) => {
                    const n = parsePercentageInput(value);
                    if (isNaN(n) || value === '')
                      return 'Informe um percentual válido';
                    if (n < minCommission || n > maxCommission)
                      return `Percentual deve ser entre ${minCommission} e ${maxCommission}`;
                    return undefined;
                  },
                }}
                children={field => (
                  <RequiredFieldTooltip active>
                  <Field.Root invalid={!!field.state.meta.errors.length}>
                    <Field.Label>
                      Percentual de Corretagem do seguro
                      <span className='text-alba-error-red-500'>*</span>
                    </Field.Label>
                    <Field.Input
                      type='text'
                      inputMode='decimal'
                      pattern='[0-9]*[.,]?[0-9]*'
                      value={field.state.value || ''}
                      onChange={e =>
                        field.handleChange(
                          sanitizePercentageInput(e.target.value)
                        )
                      }
                      onBlur={field.handleBlur}
                      placeholder='% XXX'
                      disabled={isViewMode}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <Field.ErrorText>
                        {field.state.meta.errors[0]}
                      </Field.ErrorText>
                    )}
                  </Field.Root>
                  </RequiredFieldTooltip>
                )}
              />
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              <form.AppField
                name='brokerName'
                children={field => (
                  <field.TextField
                    label='Corretor'
                    placeholder='Nome do Corretor'
                    disabled
                  />
                )}
              />

              <form.Subscribe
                selector={state => state.values.includeCoBrokerage}
                children={includeCoBrokerage => (
                  <form.AppField
                    name='participationPercent'
                    validators={{
                      onChange: ({ value }) => {
                        setParticipationSumError(null);
                        const n = parsePercentageInput(value);
                        if (isNaN(n) || value === '')
                          return 'Informe um percentual válido';
                        if (n < 0 || n > 100)
                          return 'Percentual deve ser entre 0 e 100';
                        return undefined;
                      },
                    }}
                    children={field => (
                      <RequiredFieldTooltip active>
                      <Field.Root invalid={!!field.state.meta.errors.length}>
                        <Field.Label>
                          Percentual de Participação
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          type='text'
                          inputMode='decimal'
                          pattern='[0-9]*[.,]?[0-9]*'
                          value={
                            includeCoBrokerage
                              ? field.state.value || ''
                              : '100,00'
                          }
                          onChange={e => {
                            setParticipationSumError(null);
                            field.handleChange(
                              sanitizePercentageInput(e.target.value)
                            );
                          }}
                          onBlur={field.handleBlur}
                          placeholder='% 100,00'
                          disabled={isViewMode || !includeCoBrokerage}
                        />
                        {field.state.meta.errors.length > 0 && (
                          <Field.ErrorText>
                            {field.state.meta.errors[0]}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>
                    )}
                  />
                )}
              />
            </div>

            <form.AppField
              name='includeCoBrokerage'
              children={field => (
                <field.CheckboxField
                  label='Incluir co-corretagem?'
                  disabled={isViewMode}
                />
              )}
            />

            <form.Subscribe
              selector={state => state.values.includeCoBrokerage}
              children={includeCoBrokerage =>
                includeCoBrokerage && (
                  <div className='flex flex-col gap-4'>
                    {coBrokers.map(coBroker => (
                      <div key={coBroker.id} className='flex flex-col gap-4'>
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                          <RequiredFieldTooltip active>
                          <Field.Root
                            invalid={Boolean(
                              coBrokerErrors[coBroker.id]?.coBroker
                            )}
                          >
                            <div className='mb-2 flex items-center justify-between gap-3'>
                              <Field.Label className='mb-0'>
                                Co-corretor
                                <span className='text-alba-error-red-500'>*</span>
                              </Field.Label>
                              <button
                                type='button'
                                onClick={() => handleRemoveCoBroker(coBroker.id)}
                                className='flex shrink-0 items-center gap-1 text-sm text-alba-gray-600 hover:text-alba-error-red-500'
                                disabled={
                                  isViewMode ||
                                  isPending ||
                                  calculateMutation.isPending
                                }
                                aria-label='Remover co-corretor'
                              >
                                <Trash2 className='h-4 w-4' strokeWidth={1.6} />
                                Remover
                              </button>
                            </div>
                            <Select.Root
                              value={
                                coBroker.coBrokerId
                                  ? [coBroker.coBrokerId]
                                  : []
                              }
                              onValueChange={details =>
                                handleCoBrokerSelection(
                                  coBroker.id,
                                  details.value[0] || ''
                                )
                              }
                              positioning={{ sameWidth: true }}
                              items={brokerOptions}
                              disabled={
                                isViewMode ||
                                isPending ||
                                calculateMutation.isPending
                              }
                            >
                              <Select.Control>
                                <Select.Trigger className='min-w-0'>
                                  <Select.ValueText
                                    placeholder={
                                      isLoadingBrokers
                                        ? 'Carregando corretores...'
                                        : 'Selecione o co-corretor'
                                    }
                                    className='min-w-0 truncate'
                                  />
                                  <Select.Indicator />
                                </Select.Trigger>
                              </Select.Control>
                              <Select.Positioner>
                                <Select.Content>
                                  {brokerOptions.map(item => (
                                    <Select.Item key={item.value} item={item}>
                                      <Select.ItemText>
                                        {item.label}
                                      </Select.ItemText>
                                      <Select.ItemIndicator />
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Positioner>
                            </Select.Root>
                            {coBrokerErrors[coBroker.id]?.coBroker && (
                              <Field.ErrorText>
                                {coBrokerErrors[coBroker.id].coBroker}
                              </Field.ErrorText>
                            )}
                          </Field.Root>
                          </RequiredFieldTooltip>

                          <RequiredFieldTooltip active>
                          <Field.Root
                            invalid={Boolean(
                              coBrokerErrors[coBroker.id]?.participationPercent
                            )}
                          >
                            <div className='mb-2 flex items-center justify-between gap-3'>
                              <Field.Label className='mb-0'>
                                Percentual de Participação
                                <span className='text-alba-error-red-500'>*</span>
                              </Field.Label>
                            </div>
                            <Field.Input
                              type='text'
                              inputMode='decimal'
                              pattern='[0-9]*[.,]?[0-9]*'
                              value={coBroker.participationPercent}
                              onChange={e =>
                                handleCoBrokerChange(
                                  coBroker.id,
                                  'participationPercent',
                                  e.target.value
                                )
                              }
                              placeholder='% XXX'
                              min={0}
                              max={100}
                              step='0.01'
                              disabled={
                                isViewMode ||
                                isPending ||
                                calculateMutation.isPending
                              }
                            />
                            {coBrokerErrors[coBroker.id]
                              ?.participationPercent && (
                              <Field.ErrorText>
                                {
                                  coBrokerErrors[coBroker.id]
                                    .participationPercent
                                }
                              </Field.ErrorText>
                            )}
                          </Field.Root>
                          </RequiredFieldTooltip>
                        </div>
                      </div>
                    ))}

                    <button
                      type='button'
                      onClick={handleAddCoBroker}
                      className='flex items-center gap-2 text-alba-purple-500 hover:text-alba-purple-600 font-medium text-sm'
                      disabled={isViewMode}
                    >
                      <Plus className='w-4 h-4' strokeWidth={1.6} />
                      Adicionar Co-corretor
                    </button>
                  </div>
                )
              }
            />

            {participationSumError && (
              <Alert.Root status='error'>
                <Alert.Icon />
                <Alert.Content>
                  <Alert.Description>{participationSumError}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
          </div>
        </div>
        <div className='flex flex-row items-center justify-between gap-3 self-stretch sm:gap-6 sm:px-6'>
          <Button
            variant='ghost'
            size='md'
            className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 sm:h-auto'
            onClick={e => {
              e.preventDefault();
              onBack();
            }}
          >
            <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
            Voltar
          </Button>
          <Button
            variant='primary'
            size='md'
            className='flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl px-5 py-2 sm:h-auto sm:min-w-[148px] sm:px-6'
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit();
            }}
            disabled={
              isSubmitting ||
              (!skipBackendSubmit && (isPending || calculateMutation.isPending))
            }
          >
            {isSubmitting ||
            (!skipBackendSubmit &&
              (isPending || calculateMutation.isPending)) ? (
              <>
                Calculando...
                <Spinner size='sm' colorScheme='gray' />
              </>
            ) : (
              <>
                Calcular
                <Calculator className='h-5 w-5' strokeWidth={1.6} />
              </>
            )}
          </Button>
        </div>

        <Dialog.Root
          open={rnsHighClaimDialogOpen}
          onOpenChange={details => setRnsHighClaimDialogOpen(details.open)}
        >
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content className='max-w-md w-full'>
              <Dialog.Header>
                <Dialog.Title>RNS — histórico de sinistros</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body className='flex flex-col gap-4'>
                <Text className='text-sm text-alba-gray-700'>
                  Sinistro acima de R$ 10.000,00 identificado no RNS. Cotação
                  fora da aceitação automática.
                </Text>
              </Dialog.Body>
              <Dialog.Footer className='flex justify-end gap-3'>
                <Button
                  variant='primary'
                  size='md'
                  onClick={() => {
                    const nextId = pendingNavigateAfterRnsWarningRef.current;
                    pendingNavigateAfterRnsWarningRef.current = null;
                    setRnsHighClaimDialogOpen(false);
                    if (nextId) {
                      onNext(nextId);
                    }
                  }}
                >
                  Continuar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      </div>
    );
  },
});
